import {
  columnVisibilityFeature,
  createPaginatedRowModel,
  createSortedRowModel,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from "@tanstack/react-table";

/**
 * The single feature set every DataTable in this app is built from.
 *
 * react-table v9 replaced v8's "pass a getXRowModel() option" approach with
 * static feature composition: the features object is stitched into the table at
 * the type level, and `TFeatures` became the *first* generic parameter of
 * `ColumnDef`, `Column`, `Table`, and friends. Declaring the set once here keeps
 * that parameter from having to be threaded through every call site by hand.
 *
 * Defined at module scope on purpose — the v9 docs call this out, since
 * rebuilding the object per render would remount the table's internals.
 *
 * Only the features the DataTable actually uses are registered: row selection,
 * sorting and pagination. Filtering and search are handled server-side by the
 * API (see `.claude/rules/api.md`), not by react-table, so the filtering
 * features are deliberately absent.
 *
 * Note that `sortedRowModel` and `paginatedRowModel` are always present, where
 * v8 passed `undefined` for them in server-side mode. Feature registration is
 * static in v9, so the switch now lives entirely in the `manualSorting` /
 * `manualPagination` options, which short-circuit the models before they run.
 * Behaviour is unchanged; the models are simply inert when manual mode is on.
 */
export const dataTableFeatures = tableFeatures({
  // Registered for `row.getVisibleCells()`, which was core in v8 but moved into
  // this feature in v9. There is no column-visibility UI today, so with the
  // default state (everything visible) it behaves exactly as v8 did; keeping the
  // feature also keeps a column's `enableHiding` meaningful.
  columnVisibilityFeature,
  rowSelectionFeature,
  rowSortingFeature,
  rowPaginationFeature,
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  // Registry keys become the valid string values for a column's `sortFn`.
  // The app relies on automatic sort-type detection, so these are just the
  // stock functions rather than anything custom.
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
});

/** Feature-set type to use as the first generic argument of v9 table types. */
export type DataTableFeatures = typeof dataTableFeatures;
